import React, { useCallback, useEffect, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import { CalendarCheck } from 'lucide-react';
import { PageHeader, DataTable, Person, Modal, Button } from '../../components';
import { agencyService } from '../../services/agencyService';
import {
  FilterTabs, BookingPill, ReasonModal, Field, errorMessage, dayLabel, timeRange, money, clockOf,
  hoursOf, stampOf, longDay, ActivityChips, punctuality, agencyCanCancel, CancelBookingModal,
} from './shared';

/**
 * Agency > Bookings — every named offer and booking across the agency's care
 * homes. A booking is one worker on one dated shift; the home confirms it,
 * the worker clocks it in the app with the home's QR code, and the home
 * approves the hours (Agency > Hours).
 */

const VIEWS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'offers', label: 'Offers awaiting the home' },
  { value: 'completed', label: 'Worked' },
  { value: 'closed', label: 'Cancelled / turned down' },
];

export const BookingDetail = ({ booking, onClose, actions }) => {
  if (!booking) return null;
  const a = booking.actual;
  return (
    <Modal isOpen onClose={onClose} title={`Booking ${booking.reference}`} maxWidth="max-w-lg" footer={actions}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <Person name={booking.worker?.name} sub={[booking.worker?.user_id, booking.job_role].filter(Boolean).join(' · ')} image={booking.worker?.avatar_url} />
          <BookingPill booking={booking} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Care home">{booking.home?.name}</Field>
          <Field label="Agency">{booking.agency?.name}</Field>
          <Field label="Date">{longDay(booking.shift_date)}</Field>
          <Field label="Booked">{timeRange(booking)}</Field>
          {booking.unit && <Field label="Unit">{booking.unit}</Field>}
          <Field label="Hourly charge">{money(booking.hourly_rate)}</Field>
          {a?.clock_in && (
            <>
              <Field label="Clocked in">{clockOf(a.clock_in)}</Field>
              <Field label="Clocked out">{a.clock_out ? clockOf(a.clock_out) : 'On shift'}</Field>
              <Field label="Break">{a.break_minutes} min</Field>
              <Field label="Worked">{hoursOf(a.worked_minutes)}</Field>
              <Field label="Hours">{booking.hours === 'approved' ? `Approved ${stampOf(a.hours_approved_at)}` : booking.hours === 'to_approve' ? 'Waiting for the home' : '—'}</Field>
              <Field label="Charge">{money(booking.charge)}</Field>
            </>
          )}
          {booking.reason && <div className="col-span-2"><Field label="Note">{booking.reason}</Field></div>}
          {a?.clock_in && booking.attendance && (
            <div className="col-span-2">
              <Field label="Punctuality">{punctuality(booking.attendance) || 'On time'}</Field>
            </div>
          )}
          {booking.activity && (
            <div className="col-span-2">
              <Field label="Recorded during this shift">
                <ActivityChips activity={booking.activity} />
              </Field>
              <p className="mt-1 text-[11.5px] text-[#94A3B8]">Counts only — the records themselves belong to the care home.</p>
            </div>
          )}
        </div>
        {booking.history?.length > 0 && (
          <div>
            <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300 mb-2">History</p>
            <ul className="flex flex-col gap-1.5">
              {booking.history.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                  <span className="font-semibold text-[#334155] dark:text-gray-300 capitalize">{String(h.action).replace(/_/g, ' ')}</span>
                  {h.by_name ? ` by ${h.by_name}` : ''}{h.by_agency ? ' (agency)' : ''} · {stampOf(h.at)}{h.reason ? ` — ${h.reason}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {a?.hours_change_log?.length > 0 && (
          <div>
            <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300 mb-2">Hours changes</p>
            <ul className="flex flex-col gap-1.5">
              {a.hours_change_log.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                  {h.from ? `${clockOf(h.from.start)}–${clockOf(h.from.end)} → ` : ''}{clockOf(h.to?.start)}–{clockOf(h.to?.end)}
                  {h.by_name ? ` · ${h.by_name}` : ''} · {stampOf(h.at)}{h.note ? ` — ${h.note}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

const AgencyBookings = () => {
  const [view, setView] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await agencyService.bookings({ view });
      setRows(res.data || []);
      setCounts(res.meta?.counts || {});
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load bookings.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const columns = [
    { key: 'shift_date', header: 'Shift', sortable: true, render: (b) => <span className="text-[13px] font-semibold">{dayLabel(b.shift_date)} · {timeRange(b)}</span> },
    { key: 'home', header: 'Care home', render: (b) => <span className="text-[13px]">{b.home?.name}{b.unit ? <span className="text-[#94A3B8]"> · {b.unit}</span> : ''}</span> },
    { key: 'worker', header: 'Staff', render: (b) => <Person name={b.worker?.name} sub={b.worker?.user_id} image={b.worker?.avatar_url} /> },
    { key: 'rate', header: 'Charge', render: (b) => `${money(b.hourly_rate)}/h` },
    { key: 'state', header: 'Status', render: (b) => <BookingPill booking={b} /> },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Bookings" subtitle="Your staff's offers and confirmed shifts at every care home." />
      <FilterTabs value={view} onChange={setView} tabs={VIEWS.map((v) => ({ ...v, count: counts[v.value] }))} />
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        rowKey={(b) => b.id}
        onRowClick={setOpen}
        emptyIcon={CalendarCheck}
        emptyTitle="No bookings here"
        emptyText="Offers you send from Requests appear here until the care home decides."
      />
      <BookingDetail
        booking={open}
        onClose={() => setOpen(null)}
        actions={open?.status === 'offered' ? (
          <div className="flex justify-end">
            <Button variant="danger-soft" onClick={() => { setWithdrawing(open); setOpen(null); }}>Withdraw offer</Button>
          </div>
        ) : agencyCanCancel(open) ? (
          <div className="flex justify-end">
            <Button variant="danger-soft" onClick={() => { setCancelling(open); setOpen(null); }}>Cancel booking</Button>
          </div>
        ) : null}
      />
      <CancelBookingModal booking={cancelling} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); }} />
      <ReasonModal
        open={Boolean(withdrawing)}
        title={`Withdraw ${withdrawing?.worker?.name || ''}`}
        confirmLabel="Withdraw offer"
        onClose={() => setWithdrawing(null)}
        onSubmit={async (reason) => {
          const res = await agencyService.withdrawOffer(withdrawing.id, reason);
          toast.success(res.message);
          setWithdrawing(null);
          load();
        }}
      />
    </div>
  );
};

export default AgencyBookings;
